import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useFragment } from "react-relay";

import { DropdownSelectOption } from "@whoosh/design-system/src/components/controls/DropdownSelect";
import { useGetClubCurrency } from "@whoosh/features/src/ClubCurrency";
import { ConvertCentsToDollars } from "@whoosh/utilities/src/Currency";

import { getBookingRateAtom } from "../../../../../atoms/selectors/getBookingRateAtom/getBookingRateAtom";
import { useGetDurationBookingRateOptions_Booking$key } from "./__generated__/useGetDurationBookingRateOptions_Booking.graphql";

type Props = {
  bookingRef: useGetDurationBookingRateOptions_Booking$key | null;
};

export const useGetDurationBookingRateOptions = ({ bookingRef }: Props) => {
  const { currencySymbol } = useGetClubCurrency();
  const booking = useFragment(
    useGetDurationBookingRateOptionsString,
    bookingRef
  );
  const slot = booking?.startSlot;
  const bookingRates = slot?.bookingRates;
  const currentRate = useAtomValue(getBookingRateAtom);

  const isCurrentRateLocked = currentRate?.isLocked ?? false;

  const currentTotalDurationPrice =
    bookingRates?.find((rate) => rate?.rate?.id === currentRate?.id)
      ?.totalDurationPrice ?? 0;

  const singleOption: DropdownSelectOption<string> = {
    value: currentRate?.id ?? "",
    label: currentRate?.name || "",
    subLabel: `${currencySymbol}${ConvertCentsToDollars(
      currentTotalDurationPrice
    )}`,
  };

  const dropdownOptions: DropdownSelectOption<string>[] =
    bookingRates?.flatMap((rate) => {
      if (!rate?.rate?.id) return [];
      const priceInCents = rate?.totalDurationPrice ?? 0;
      const priceDisplay = ConvertCentsToDollars(priceInCents ?? 0);
      return {
        value: rate.rate.id,
        label: rate.rate.name || "",
        subLabel: `${currencySymbol}${priceDisplay}`,
      };
    }) ?? [];

  return isCurrentRateLocked ? [singleOption] : dropdownOptions;
};

const useGetDurationBookingRateOptionsString = graphql`
  fragment useGetDurationBookingRateOptions_Booking on Booking {
    startSlot {
      id
      ... on TimeSlot {
        bookingRates(
          highestPrecedentOnly: false
          type: CUSTOMER
          visibleToMembershipTypeId: $membershipTypeId
        ) {
          rate {
            id
            name
            visibility
            appliesToMembershipType(membershipTypeId: $membershipTypeId)
          }
          totalDurationPrice(duration: $duration, playerCount: $groupSize)
        }
      }
    }
  }
`;
